import React, { FC, useMemo } from "react";
import { endOfWeek, format, parseISO, startOfWeek } from "date-fns";
import { useTranslation } from "react-i18next";
import { useGetTasksExecutionQuery } from "shared/api";
import { OverviewThisWeekTasksContent } from "./OverviewThisWeekTasksContent";

const OverviewThisWeekTasks: FC = () => {
  const { t } = useTranslation();

  const { timestamp_gte, timestamp_lte } = useMemo(() => {
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const end = endOfWeek(now, { weekStartsOn: 1 });
    return {
      timestamp_gte: format(start, "yyyy-MM-dd'T'00:00:00"),
      timestamp_lte: format(end, "yyyy-MM-dd'T'23:59:59"),
    };
  }, []);

  const { data: raw = [], isLoading } = useGetTasksExecutionQuery(
    {
      timestamp_expected__gte: timestamp_gte,
      timestamp_expected__lte: timestamp_lte,
      expand: "task,machine,user",
      ordering: "timestamp_expected",
    },
    { refetchOnMountOrArgChange: true }
  );

  const tasks = useMemo(() => {
    const startMs = parseISO(timestamp_gte).getTime();
    const endMs = parseISO(timestamp_lte).getTime();
    return [...raw]
      .filter((row) => {
        const time = parseISO(row.timestamp_expected).getTime();
        return time >= startMs && time <= endMs;
      })
      .sort(
        (a, b) =>
          parseISO(a.timestamp_expected).getTime() -
          parseISO(b.timestamp_expected).getTime()
      );
  }, [raw, timestamp_gte, timestamp_lte]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white">
      <div className="shrink-0 border-b border-neutral-200 bg-neutral-100 py-2.5 text-center">
        <span className="text-sm font-semibold text-neutral-900">
          {t("overview.thisWeek")}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <OverviewThisWeekTasksContent
          isLoading={isLoading}
          tasks={tasks}
          machinesNaLabel={t("taskHub.machines.na")}
          unnamedTaskLabel={t("overview.unnamedTask")}
          emptyDescription={t("overview.noWeekTasks")}
        />
      </div>
    </div>
  );
};

export { OverviewThisWeekTasks };
